import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, X, Sparkles, MapPin, UserPlus, Ban, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import EmployeeCard from "@/components/EmployeeCard";
import { deptBadgeClass, effectiveSkills } from "@/lib/employees";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOCATIONS = ["București", "Cluj"];
const MAX_VISIBLE_SKILL_FILTERS = 12;

function AddColleagueDialog({ open, onOpenChange, onCreated, allDepts }) {
  const [form, setForm] = useState({ name: "", role: "", department: "", email: "", phone: "", location: "", is_collaborator: false });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Numele este obligatoriu");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/employees`, form);
      toast.success(`${r.data.name} a fost adăugat!`);
      onCreated?.(r.data);
      setForm({ name: "", role: "", department: "", email: "", phone: "", location: "", is_collaborator: false });
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la creare");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => onOpenChange(false)}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="add-colleague-dialog" className="bg-white dark:bg-zinc-900 rounded-2xl border border-border shadow-2xl max-w-md w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"><UserPlus className="h-4 w-4" /></span>
          <h3 className="text-lg font-semibold">Adaugă coleg nou</h3>
        </div>
        {[
          { k: "name", label: "Nume complet *", ph: "Ex: POPESCU MARIA" },
          { k: "role", label: "Rol", ph: "Ex: Programator" },
          { k: "email", label: "Email", ph: "nume.prenume@ezipper.ro", type: "email" },
          { k: "phone", label: "Telefon", ph: "0712 345 678" },
        ].map(({ k, label, ph, type }) => (
          <label key={k} className="block">
            <span className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">{label}</span>
            <input
              data-testid={`new-emp-${k}`}
              type={type || "text"}
              value={form[k]}
              onChange={(e) => update(k, e.target.value)}
              placeholder={ph}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900"
            />
          </label>
        ))}
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">Departament</span>
          <select
            data-testid="new-emp-department"
            value={form.department}
            onChange={(e) => update("department", e.target.value)}
            className="mt-1 w-full h-10 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900"
          >
            <option value="">— alege —</option>
            {(allDepts || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">Locație</span>
          <div className="mt-1 inline-flex items-center gap-1 p-1 rounded-full border border-border bg-zinc-50 dark:bg-zinc-900">
            <button type="button" onClick={() => update("location", "")} className={`px-3 py-1 text-xs rounded-full ${form.location === "" ? "bg-white dark:bg-zinc-700 shadow-sm" : "text-zinc-500"}`}>—</button>
            {LOCATIONS.map((l) => (
              <button key={l} type="button" data-testid={`new-emp-loc-${l}`} onClick={() => update("location", l)} className={`px-3 py-1 text-xs rounded-full ${form.location === l ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "text-zinc-500"}`}>{l}</button>
            ))}
          </div>
        </label>
        <label className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40 cursor-pointer">
          <input
            data-testid="new-emp-collaborator"
            type="checkbox"
            checked={form.is_collaborator}
            onChange={(e) => update("is_collaborator", e.target.checked)}
            className="h-4 w-4 rounded border-border accent-amber-500"
          />
          <span className="flex flex-col">
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Este colaborator</span>
            <span className="text-[11px] text-zinc-500 dark:text-zinc-400">Persoană externă asociată departamentului (nu angajat intern)</span>
          </span>
        </label>
        <div className="flex items-center gap-2 pt-2">
          <button type="submit" disabled={busy} data-testid="new-emp-submit" className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50">
            <UserPlus className="h-4 w-4" /> {busy ? "Se creează..." : "Adaugă coleg"}
          </button>
          <button type="button" onClick={() => onOpenChange(false)} className="inline-flex items-center px-4 h-10 rounded-lg border border-border text-sm">Anulează</button>
        </div>
      </form>
    </div>
  );
}

export default function Directory({ employees, allSkills, onOpenProfile, activeSkill, onSetActiveSkill, onEmployeeCreated }) {
  const [query, setQuery] = useState("");
  const [activeDept, setActiveDept] = useState("Toti");
  const [activeLocation, setActiveLocation] = useState("Toate");
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [hideInactive, setHideInactive] = useState(true);
  const [mentorOnly, setMentorOnly] = useState(false);

  const inactiveCount = useMemo(() => employees.filter((e) => e.inactive).length, [employees]);
  const mentorCount = useMemo(() => employees.filter((e) => e.mentor_active && e.mentor_status === "available").length, [employees]);

  const departments = useMemo(() => {
    const counts = new Map();
    employees.forEach((e) => counts.set(e.department, (counts.get(e.department) || 0) + 1));
    return [
      { name: "Toti", count: employees.length, label: "Toți" },
      ...Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count, label: name })),
    ];
  }, [employees]);

  const allDeptNames = useMemo(() => departments.filter((d) => d.name !== "Toti").map((d) => d.name), [departments]);

  const locationCounts = useMemo(() => {
    let buc = 0, cluj = 0, none = 0;
    employees.forEach((e) => { if (e.location === "București") buc++; else if (e.location === "Cluj") cluj++; else none++; });
    return { "București": buc, "Cluj": cluj, "Fără locație": none };
  }, [employees]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees
      .filter((e) => !hideInactive || !e.inactive)
      .filter((e) => !mentorOnly || (e.mentor_active && e.mentor_status === "available"))
      .filter((e) => activeDept === "Toti" || e.department === activeDept)
      .filter((e) => {
        if (activeLocation === "Toate") return true;
        if (activeLocation === "Fără locație") return !e.location;
        return e.location === activeLocation;
      })
      .filter((e) => !activeSkill || effectiveSkills(e).some((s) => s.toLowerCase() === activeSkill.toLowerCase()))
      .filter((e) => !q || e.name.toLowerCase().includes(q) || e.role.toLowerCase().includes(q) || e.department.toLowerCase().includes(q) || (e.email || "").toLowerCase().includes(q) || effectiveSkills(e).some((s) => s.toLowerCase().includes(q)) || (e.hobbies || []).some((h) => h.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name, "ro"));
  }, [employees, query, activeDept, activeLocation, activeSkill, hideInactive, mentorOnly]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        e.preventDefault();
        document.querySelector('[data-testid="search-input"]')?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const visibleSkills = showAllSkills ? allSkills : allSkills.slice(0, MAX_VISIBLE_SKILL_FILTERS);

  return (
    <div data-testid="directory-view" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium">Director angajați</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">Cunoaște-ți colegii.</h1>
          <p className="mt-3 text-zinc-600 dark:text-zinc-400 max-w-2xl">
            Caută pe nume, rol, departament, locație, competențe sau hobby.
          </p>
        </div>
        <button
          data-testid="add-colleague-btn"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 shadow-sm"
        >
          <UserPlus className="h-4 w-4" /> Adaugă coleg
        </button>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <input
          data-testid="search-input"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Caută (nume, rol, departament, competență, hobby)..."
          className="w-full h-12 pl-11 pr-12 rounded-xl border border-border bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
        />
        {query ? (
          <button data-testid="clear-search-btn" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800">
            <X className="h-4 w-4" />
          </button>
        ) : (
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center h-6 px-1.5 text-[10px] font-mono text-zinc-400 border border-border rounded">/</kbd>
        )}
      </div>

      <div className="mb-3 -mx-2 sm:mx-0">
        <div data-testid="department-filters" className="flex gap-2 overflow-x-auto px-2 sm:px-0 pb-2 scrollbar-hide">
          {departments.map((d) => {
            const isActive = activeDept === d.name;
            const isAll = d.name === "Toti";
            return (
              <button
                key={d.name}
                data-testid={`dept-pill-${d.name.replace(/\s+/g, "-")}`}
                onClick={() => setActiveDept(d.name)}
                className={`flex-shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 ring-1 ring-zinc-900 dark:ring-zinc-100"
                  : isAll ? "bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 ring-1 ring-inset ring-border hover:bg-zinc-50 dark:hover:bg-zinc-800"
                  : `ring-1 ring-inset ${deptBadgeClass(d.name)} hover:opacity-90`
                }`}
              >
                <span>{d.label}</span>
                <span className={`text-[11px] tabular-nums ${isActive ? "opacity-70" : "opacity-60"}`}>{d.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium mr-1">
          <MapPin className="h-3 w-3" /> Locație
        </span>
        {[{ k: "Toate", n: employees.length }, { k: "București", n: locationCounts["București"] }, { k: "Cluj", n: locationCounts["Cluj"] }, { k: "Fără locație", n: locationCounts["Fără locație"] }].map((opt) => {
          const isActive = activeLocation === opt.k;
          return (
            <button
              key={opt.k}
              data-testid={`location-pill-${opt.k}`}
              onClick={() => setActiveLocation(opt.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                isActive ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700"
              }`}
            >
              {opt.k}
              <span className={`text-[10px] ${isActive ? "opacity-70" : "opacity-60"}`}>{opt.n}</span>
            </button>
          );
        })}
        {(mentorCount > 0 || inactiveCount > 0) && (
          <div className="ml-auto flex items-center gap-2">
            {mentorCount > 0 && (
              <button
                data-testid="toggle-mentor-btn"
                onClick={() => setMentorOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  mentorOnly ? "bg-emerald-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                }`}
              >
                <GraduationCap className="h-3 w-3" />
                {mentorOnly ? "Toți colegii" : `Mentori disponibili (${mentorCount})`}
              </button>
            )}
            {inactiveCount > 0 && (
              <button
                data-testid="toggle-inactive-btn"
                onClick={() => setHideInactive((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  hideInactive ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700" : "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                }`}
              >
                <Ban className="h-3 w-3" />
                {hideInactive ? `Arată inactivii (${inactiveCount})` : "Ascunde inactivii"}
              </button>
            )}
          </div>
        )}
      </div>

      {allSkills && allSkills.length > 0 && (
        <div className="mb-6 -mx-2 sm:mx-0">
          <div className="flex items-center gap-2 mb-2 px-2 sm:px-0">
            <Sparkles className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">Filtru competențe</span>
            {activeSkill && (
              <button data-testid="clear-skill-filter-btn" onClick={() => onSetActiveSkill(null)} className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline-offset-2 hover:underline">Resetează</button>
            )}
          </div>
          <div data-testid="skill-filters" className="flex flex-wrap gap-2 px-2 sm:px-0">
            {visibleSkills.map((s) => {
              const isActive = activeSkill && activeSkill.toLowerCase() === s.name.toLowerCase();
              return (
                <button
                  key={s.name}
                  data-testid={`skill-filter-${s.name}`}
                  onClick={() => onSetActiveSkill(isActive ? null : s.name)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    isActive ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                  }`}
                >
                  <span>{s.name}</span>
                  <span className={`text-[10px] ${isActive ? "opacity-70" : "opacity-50"}`}>{s.count}</span>
                </button>
              );
            })}
            {allSkills.length > MAX_VISIBLE_SKILL_FILTERS && (
              <button data-testid="toggle-skills-btn" onClick={() => setShowAllSkills(!showAllSkills)} className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100">
                {showAllSkills ? "Mai puține" : `+${allSkills.length - MAX_VISIBLE_SKILL_FILTERS} mai multe`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 flex items-baseline justify-between">
        <p data-testid="results-count" className="text-sm text-zinc-500 dark:text-zinc-400">
          {filtered.length} {filtered.length === 1 ? "rezultat" : "rezultate"}
          {activeSkill && <span> · filtrat după <strong className="text-zinc-700 dark:text-zinc-300">{activeSkill}</strong></span>}
          {activeLocation !== "Toate" && <span> · {activeLocation}</span>}
        </p>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="no-results" className="text-center py-20 border border-dashed border-border rounded-xl">
          <p className="text-zinc-500 dark:text-zinc-400">Niciun angajat nu corespunde căutării.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((e) => (
            <EmployeeCard key={e.id} employee={e} onOpen={onOpenProfile} onSkillClick={onSetActiveSkill} />
          ))}
        </div>
      )}

      <AddColleagueDialog open={addOpen} onOpenChange={setAddOpen} onCreated={onEmployeeCreated} allDepts={allDeptNames} />
    </div>
  );
}
